import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useChanceyTheme } from '../../theme/ThemeContext';
import { Icon } from '../chancey/Icons';
import { withSafeArea } from './safeArea';

export type ShellTabId = 'home' | 'stats' | 'scan' | 'rules' | 'settings';

const TAB_PATH: Record<ShellTabId, string> = {
  home: '/home',
  stats: '/stats',
  scan: '/scan',
  rules: '/rules',
  settings: '/settings',
};

export const AppTabBar: React.FC = () => {
  const { c, typ, dark } = useChanceyTheme();
  const history = useHistory();
  const location = useLocation();

  const activeFromPath = (): ShellTabId => {
    const p = location.pathname;
    if (p.startsWith('/stats')) return 'stats';
    if (p.startsWith('/scan')) return 'scan';
    if (p.startsWith('/rules')) return 'rules';
    if (p.startsWith('/settings')) return 'settings';
    return 'home';
  };

  const active = activeFromPath();

  const onTab = (id: ShellTabId) => {
    history.push(TAB_PATH[id]);
  };

  const tabs: {
    id: ShellTabId;
    label: string;
    icon: (s: number, color: string) => React.ReactNode;
    center?: boolean;
  }[] = [
    { id: 'home', label: 'Home', icon: Icon.home },
    { id: 'stats', label: 'Stats', icon: Icon.stats },
    { id: 'scan', label: 'Scan', icon: Icon.cam, center: true },
    { id: 'rules', label: 'Builder', icon: Icon.wand },
    { id: 'settings', label: 'Settings', icon: Icon.gear },
  ];

  return (
    <div
      className="relative z-40 mx-4 mb-2 mt-1 flex h-16 items-center justify-around rounded-[32px] px-2 backdrop-blur-xl"
      style={{
        /* Stronger fill so scroll content does not read through the bar */
        background: dark ? 'rgba(28,28,32,0.92)' : 'rgba(255,255,255,0.94)',
        marginLeft: withSafeArea('left', 16),
        marginRight: withSafeArea('right', 16),
        WebkitBackdropFilter: 'blur(24px) saturate(180%)',
        border: `0.5px solid ${c.line}`,
        boxShadow: dark ? '0 10px 40px rgba(0,0,0,0.4)' : '0 10px 30px rgba(0,0,0,0.08)',
      }}
    >
      {tabs.map((t) => {
        const isActive = active === t.id;
        if (t.center) {
          return (
            <button
              key={t.id}
              type="button"
              aria-label={t.label}
              className="-mt-4 flex h-[52px] w-[52px] shrink-0 cursor-pointer items-center justify-center rounded-full shadow-lg"
              style={{
                background: c.gradient,
                boxShadow: `0 6px 16px ${c.accent}60`,
              }}
              onClick={() => onTab(t.id)}
            >
              {t.icon(24, '#fff')}
            </button>
          );
        }
        return (
          <button
            key={t.id}
            type="button"
            className="flex flex-1 cursor-pointer flex-col items-center gap-0.5"
            style={{ color: isActive ? c.ink : c.inkMuted }}
            onClick={() => onTab(t.id)}
          >
            {t.icon(22, isActive ? c.ink : c.inkMuted)}
            <span
              className="text-[10px] tracking-normal"
              style={{
                fontFamily: typ.body,
                fontWeight: isActive ? 600 : 500,
              }}
            >
              {t.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
